import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import LogRocket from 'logrocket';
import { createAuthOnboardingSlice } from '@Store/slices/authOnboarding/createAuthOnboardingSlice.ts';
import { createResetPasswordSlice } from './slices/resetPassword/createResetPasswordSlice';
import { createUboSlice } from './slices/ubo/createUboSlice';
import { createCustomerSlice } from './slices/customer/createCustomerSlice';
import {
  createEddEmbeddedSlice,
  parseCustomerEdd,
} from './slices/eddQuestions/createEddEmbeddedSlice';
import { createErrorSlice } from './slices/error/createErrorSlice';
import { createKybSlice } from './slices/kyb/createKybSlice';
import { createOnboardingSlice } from './slices/onboarding/createOnboardingSlice';
import type { IAuthOnboardingSlice } from '@Store/slices/authOnboarding/authOnboarding.types.ts';
import type { IResetPasswordSlice } from './slices/resetPassword/createResetPasswordSlice';
import type { UboSlice } from './slices/ubo/createUboSlice';
import type { IErrorSlice } from '../types/error.types';
import type { IOnboardingSlice } from '../types/onboarding.types';
import type { ICustomerSlice } from './slices/customer/customer.types';
import type { IEddEmbeddedSlice } from './slices/eddQuestions/createEddEmbeddedSlice';
import type { IKybSlice } from './slices/kyb/kyb.types';

// Define the root state type
type RootType = IOnboardingSlice &
  IErrorSlice &
  ICustomerSlice &
  IAuthOnboardingSlice &
  IKybSlice &
  IResetPasswordSlice &
  UboSlice &
  IEddEmbeddedSlice & {
    isLoaded: boolean;
    setLoaded: (loaded: boolean) => void;
    isHydrated: boolean;
    setIsHydrated: (hydrated: boolean) => void;
  };

const useStore = create<RootType>()(
  devtools((...a) => ({
    ...createOnboardingSlice(...a),
    ...createErrorSlice(...a),
    ...createCustomerSlice(...a),
    ...createKybSlice(...a),
    ...createAuthOnboardingSlice(...a),
    ...createResetPasswordSlice(...a),
    ...createUboSlice(...a),
    ...createEddEmbeddedSlice(...a),
    isLoaded: false,
    isHydrated: false,
    setIsHydrated: (hydrated: boolean) => {
      useStore.setState({ isHydrated: hydrated });
    },
    setLoaded: (loaded: boolean) => {
      useStore.setState({ isLoaded: loaded });
    },
  }))
);

useStore.subscribe((state) => {
  if (state?.customer?.customerData?.customerEdd && state.eddEmbeddedData.questions.length === 0) {
    const questions = parseCustomerEdd(state?.customer?.customerData?.customerEdd);
    if (questions.length > 0) {
      useStore.setState({ eddEmbeddedData: { version: 1, questions } });
    }
  }
});

let previousCustomerId = useStore.getState().customer.customerId;
useStore.subscribe((state) => {
  const currentCustomerId = state.customer.customerId;
  if (currentCustomerId && currentCustomerId !== previousCustomerId) {
    previousCustomerId = currentCustomerId;
    LogRocket.identify(currentCustomerId);
  }
});

// HMR Support
if (import.meta.hot?.data) {
  if (import.meta.hot.data.store) {
    // Replace the current store with the stored one
    useStore.setState(import.meta.hot.data.store.getState());
  }

  import.meta.hot.accept();

  import.meta.hot.dispose((data) => {
    data.store = useStore;
  });
}

export const useBoundStore = useStore;
